<template>
  <div>
    <section class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6"></div>
        </div>
      </div>
    </section>
    <section class="content">
      <div class="container-fluid">
        <div class="row">
          <div class="col-md-12">
            <!-- Master Settings Toggle -->
            <div class="card card-success">
              <div class="card-header">
                <h3 class="card-title">Gift Card Master Settings</h3>
              </div>
              <div class="loading" v-if="loading">
                <hour-glass></hour-glass>
              </div>
              <div class="card-body">
                <div class="form-group">
                  <label class="switch">
                    <input
                      type="checkbox"
                      class="masterToggle allow-merchant-fees-report {}"
                      v-model="masterSettings.isEnabled"
                      @change="updateMasterSettings"
                    />
                    <span class="slider round"></span>
                  </label>
                  <span class="ml-2"><strong>Enable Store Gift Card Issuing</strong></span>
                </div>
              </div>
            </div>

            <!-- First-Time Enablement Confirmation Modal -->
            <b-modal
              id="firstTimeEnabledModal"
              ref="firstTimeEnabledModal"
              title="Enable CanPay Merchant Gift Card Program"
              v-model="showFirstTimeModal"
              hide-header-close
              :header-text-variant="headerTextVariant"
              no-close-on-backdrop
              no-close-on-esc
              @ok="confirmFirstTimeEnablement"
              @cancel="cancelFirstTimeEnablement"
            >
              <div class="modal-body-content">
                <div class="confirmation-icon">
                  <i class="fas fa-gift"></i>
                </div>
                <h4 class="confirmation-title">Enable CanPay Merchant Gift Card Program</h4>
                <p class="confirmation-message">
                  You are about to enable the CanPay Merchant Gift Card program for your organization. This will allow your Regional Managers, RemotePay Managers, and Store Managers to issue gift cards to consumers.
                </p>
                <p class="confirmation-note">
                  <strong>Note:</strong> You can manage individual user permissions after enabling this feature.
                </p>
                <div class="confirmation-details">
                  <h5>What this enables:</h5>
                  <ul>
                    <li>Gift card issuance capabilities for authorized users</li>
                    <li>Gift card issuance history tracking</li>
                  </ul>
                </div>
                <p class="confirmation-terms">
                  By enabling the CanPay Merchant Gift Card program, I agree to the terms and conditions related to Rewards and Gift Cards (a copy of which can be found at <a href="https://www.canpaydebit.com/about-us/merchant-rewards-terms-and-conditions-exhibit-c/" target="_blank">https://www.canpaydebit.com/about-us/merchant-rewards-terms-and-conditions-exhibit-c/</a>). CanPay will debit the value for any issued gift card from the banking associated with the store assigned to the gift card. Gift card issuance can be enabled or disabled at any time, but any previously issued gift cards will remain redeemable even when gift card issuing is disabled.
                </p>
              </div>
              <template v-slot:modal-footer="{ ok, cancel }">
                <b-button @click="cancel" variant="secondary">
                  Cancel
                </b-button>
                <b-button @click="ok" variant="success">
                  Enable Gift Card Program
                </b-button>
              </template>
            </b-modal>

            <!-- User Permissions Table -->
            <div class="card card-success" v-if="masterSettings.isEnabled">
              <div class="card-header">
                <h3 class="card-title">Gift Card User Permissions</h3>
                <div class="card-tools">
                  <b-button
                    class="btn-danger export-api-btn"
                    @click="reloadDatatable"
                    v-if="showReloadBtn"
                  >
                    <i class="fas fa-redo"></i> Reload
                  </b-button>
                </div>
              </div>
              <div class="loading" v-if="loading">
                <hour-glass></hour-glass>
              </div>
              <div class="card-body">
                <!-- User Type Filter -->
                <div class="row mb-3">
                  <div class="col-md-4">
                    <div class="form-group">
                      <label for="userTypeSelect">User Type:</label>
                      <select
                        id="userTypeSelect"
                        class="form-control"
                        v-model="selectedUserType"
                        @change="onUserTypeChange"
                      >
                        <option value="Regional Manager">Regional Manager</option>
                        <option value="Remotepay Manager">Remotepay Manager</option>
                        <option value="Store Manager">Store Manager</option>
                        <option value="Accountant">Accountant</option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Accountant Access Info Message -->
                <div class="row mb-3" v-if="selectedUserType === 'Accountant'">
                  <div class="col-md-12">
                    <div class="alert alert-info">
                      <i class="fas fa-info-circle"></i>
                      <strong>Accountant Access:</strong> Accountants will have read-only access to Gift Card Reporting and transaction history. They will NOT be able to issue gift cards. This access is for reporting and auditing purposes only.
                    </div>
                  </div>
                </div>

                <!-- Bulk Action Buttons -->
                <div class="row mb-3">
                  <div class="col-md-12">
                    <div class="btn-group" role="group">
                      <b-button
                        variant="success"
                        size="sm"
                        @click="showBulkActionConfirm('enable_all')"
                        :disabled="bulkActionInProgress"
                        title="Enable all users of this type across all pages"
                      >
                        <i class="fas fa-check-circle"></i> Enable All {{ selectedUserType }}s
                      </b-button>
                      <b-button
                        variant="warning"
                        size="sm"
                        @click="showBulkActionConfirm('enable_page')"
                        :disabled="bulkActionInProgress"
                        title="Enable only users on the current page"
                      >
                        <i class="fas fa-check"></i> Enable Current Page
                      </b-button>
                      <b-button
                        variant="danger"
                        size="sm"
                        @click="showBulkActionConfirm('disable_all')"
                        :disabled="bulkActionInProgress"
                        title="Disable all users of this type across all pages"
                      >
                        <i class="fas fa-times-circle"></i> Disable All {{ selectedUserType }}s
                      </b-button>
                    </div>
                  </div>
                </div>

                <table
                  id="giftCardUsersTable"
                  class="table"
                  style="width: 100%; white-space: normal"
                >
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Gift Card Access</th>
                    </tr>
                  </thead>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bulk Action Confirmation Modal -->
      <b-modal
        :header-text-variant="headerTextVariant"
        v-model="showBulkConfirmModal"
        title="Confirm Bulk Action"
        @ok="executeBulkAction"
        @cancel="cancelBulkAction"
        ok-title="Confirm"
        cancel-title="Cancel"
        :ok-disabled="bulkActionInProgress"
      >
        <div v-if="bulkActionDetails.action === 'enable_all'">
          <p>
            <strong>Enable all {{ selectedUserType }}s?</strong>
          </p>
          <p>
            This will enable Gift Card access for all <strong>{{ totalUsersCount }}</strong> {{ selectedUserType }}s across all pages.
          </p>
          <p class="text-muted">
            <i class="fas fa-info-circle"></i> This action cannot be undone immediately. You can disable individual users later.
          </p>
        </div>
        <div v-else-if="bulkActionDetails.action === 'disable_all'">
          <p>
            <strong>Disable all {{ selectedUserType }}s?</strong>
          </p>
          <p>
            This will disable Gift Card access for all <strong>{{ totalUsersCount }}</strong> {{ selectedUserType }}s across all pages.
          </p>
          <p class="text-muted">
            <i class="fas fa-warning"></i> Users will no longer be able to issue gift cards.
          </p>
        </div>
        <div v-else-if="bulkActionDetails.action === 'enable_page'">
          <p>
            <strong>Enable users on current page?</strong>
          </p>
          <p>
            This will enable Gift Card access for <strong>{{ currentPageUsersCount }}</strong> users on the current page.
          </p>
        </div>
        <div v-if="bulkActionInProgress" class="text-center mt-3">
          <b-spinner small class="mr-2"></b-spinner>
          <span>Processing...</span>
        </div>
      </b-modal>
    </section>
  </div>
</template>

<script>
import api from "@/api/giftcard.js";
import apiuser from "@/api/user.js";
import { HourGlass } from "vue-loading-spinner";
import Constants from "@/common/constant.js";

export default {
  name: "CPGiftCardSettings",
  data() {
    return {
      userDetails: JSON.parse(localStorage.getItem("store_user")),
      constants: Constants,
      loading: false,
      showReloadBtn: false,
      showFirstTimeModal: false,
      selectedUserType: "Regional Manager", // Default value
      masterSettings: {
        first_time_enabled: false,
        isEnabled: false
      },
      allUsersModel: {},
      headerTextVariant: "light",
      // Bulk operations state
      bulkActionInProgress: false,
      showBulkConfirmModal: false,
      bulkActionDetails: {
        action: '', // 'enable_all', 'disable_all', 'enable_page'
        userType: '',
        count: 0
      },
      totalUsersCount: 0,
      currentPageUsersCount: 0
    };
  },
  components: {
    HourGlass,
  },
  mounted() {
    var self = this;
    self.loadSettings();
    document.title = "CanPay - Gift Card Settings";
    // Make component accessible globally for inline onclick handlers
    window.giftCardComponent = this;
  },
  methods: {
    loadSettings() {
      var self = this;
      self.loading = true;
      api
        .getGiftCardSettings()
        .then(function (response) {
          if (response.code == 200) {
            self.masterSettings.first_time_enabled = !response.data.first_time_enabled && !response.data.is_gift_card_enabled ? false : true;
            self.masterSettings.isEnabled = response.data.is_gift_card_enabled;
            if (self.masterSettings.isEnabled) {
              setTimeout(function () {
                self.loadDT();
              }, 500);
            }
          } else {
            error(response.message);
          }
          self.loading = false;
        })
        .catch(function (error) {
          self.loading = false;
          error("Failed to load gift card settings");
        });
    },

    updateMasterSettings() {
      var self = this;

      // If enabling for the first time, show confirmation modal
      if (self.masterSettings.isEnabled) {
        self.showFirstTimeModal = true;
        return;
      } else {
        self.proceedWithUpdate();
      }

    },

    confirmFirstTimeEnablement() {
      var self = this;
      self.showFirstTimeModal = false;
      self.proceedWithUpdate();
    },

    cancelFirstTimeEnablement() {
      var self = this;
      self.showFirstTimeModal = false;
      self.masterSettings.isEnabled = false;
    },

    proceedWithUpdate() {
      var self = this;
      self.loading = true;

      var request = {
        is_gift_card_enabled: self.masterSettings.isEnabled
      };

      api
        .updateGiftCardSettings(request)
        .then(function (response) {
          if (response.code == 200) {
            success(response.message);
            self.masterSettings.first_time_enabled = !response.data.first_time_enabled && !response.data.is_gift_card_enabled ? false : true;
            // Update localStorage to reflect the new setting
            localStorage.setItem("gift_card_enabled", self.masterSettings.isEnabled ? '1' : '0');

            // Option 2: If you prefer not to reload, emit event to parent
            self.$root.$emit('giftCardSettingsUpdated', self.masterSettings.isEnabled);

            if (self.masterSettings.isEnabled) {
              setTimeout(function () {
                self.loadDT();
              }, 500);
            }
          } else {
            error(response.message);
            // Revert the toggle
            self.masterSettings.isEnabled = !self.masterSettings.isEnabled;
          }
          self.loading = false;
        })
        .catch(function (err) {
          self.loading = false;
          if (err.response && err.response.data && err.response.data.message) {
            error(err.response.data.message);
          } else {
            error("Failed to update settings");
          }
          // Revert the toggle
          self.masterSettings.isEnabled = !self.masterSettings.isEnabled;
        });
    },

    reloadDatatable() {
      var self = this;
      self.showReloadBtn = false;
      $("#giftCardUsersTable").DataTable().ajax.reload();
    },

    onUserTypeChange() {
      var self = this;
      // Reload the DataTable with new user type filter
      if ($.fn.DataTable.isDataTable('#giftCardUsersTable')) {
        $("#giftCardUsersTable").DataTable().ajax.reload();
      } else {
        self.loadDT();
      }
    },

    loadDT() {
      var self = this;
      $("#giftCardUsersTable").DataTable({
        pagingType: "simple_numbers",
        processing: true,
        serverSide: true,
        destroy: true,
        columnDefs: [
          { orderable: false, targets: [3] },
          { className: "dt-left", targets: [0, 1, 2] },
          { className: "dt-center", targets: [3] },
        ],
        order: [[0, "asc"]],
        orderClasses: false,
        language: {
          processing:
            '<i class="fa fa-spinner fa-spin fa-3x fa-fw"></i><span class="sr-only">Loading...</span> ',
          emptyTable: "No Users Available.",
          search: "_INPUT_",
          searchPlaceholder: "Search records",
          oPaginate: {
            sNext: '<i class="fas fa-angle-double-right"></i>',
            sPrevious: '<i class="fas fa-angle-double-left"></i>',
          },
          sLengthMenu:
            "<label class='label_dropdown_dt'>Per page</label> _MENU_",
        },
        ajax: {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
          url: process.env.VUE_APP_API_URL + "/merchantadmin/getallstoreusers",
          type: "POST",
          dataType: "json",
          data: function(d) {
            d.user_type = self.selectedUserType;
            d.cal_from_giftcard = true;
            return d;
          },
          dataSrc: function (result) {
            self.allUsersModel = result.data;
            self.totalUsersCount = result.recordsTotal;
            return result.data;
          },
          error: function (request) {
            if (
              request.responseJSON.code == 401 &&
              request.responseJSON.data != null
            ) {
              localStorage.setItem("token", request.responseJSON.data);
              self.loadDT();
            } else {
              self.$toast.error(Constants.datatable_error);
              $('#giftCardUsersTable_processing').hide();
            }
          },
        },
        columns: [
          {
            render: function (data, type, full, meta) {
              return full.first_name + ' ' + full.last_name;
            }
          },
          { data: "email" },
          { data: "role_name" },
          {
            render: function (data, type, full, meta) {
              const isEnabled = full.gift_card_enabled === 1 || full.gift_card_enabled === true;
              const switchId = 'switch-' + full.user_id;
              return `
               <label class="switch">
                  <input
                    type="checkbox"
                    class="gift-card-switch"
                    id="${switchId}"
                    data-user-id="${full.user_id}"
                    ${isEnabled ? 'checked' : ''}
                  />
                  <span class="slider round"></span>
                </label>
              `;
            },
          },
        ],
        drawCallback: function() {
          // Bind switch change events
          $('.gift-card-switch').off('change').on('change', function() {
            const userId = $(this).data('user-id');
            const isEnabled = $(this).is(':checked');
            self.updateUserPermission(userId, isEnabled);
          });
        }
      });
    },

    updateUserPermission(userId, isEnabled) {
      var self = this;

      var request = {
        user_permissions: [{
          user_id: userId,
          is_enabled: isEnabled
        }]
      };

      api
        .updateUserPermissions(request)
        .then(function (response) {
          if (response.code == 200) {
            success(response.message);
          } else {
            error(response.message);
            // Revert the switch
            $(`#switch-${userId}`).prop('checked', !isEnabled);
          }
        })
        .catch(function (err) {
          if (err.response && err.response.data && err.response.data.message) {
            error(err.response.data.message);
          } else {
            error("Failed to update user permission");
          }
          // Revert the switch
          $(`#switch-${userId}`).prop('checked', !isEnabled);
        });
    },

    showBulkActionConfirm(action) {
      var self = this;

      // Calculate counts based on action
      if (action === 'enable_all' || action === 'disable_all') {
        // Count total users of selected type
        // self.totalUsersCount = self.allUsersModel.length || 0;
        // For enable_all/disable_all, we need to get total count from server
        // This will be updated when we fetch the data
      } else if (action === 'enable_page') {
        // Count users on current page
        self.currentPageUsersCount = self.allUsersModel.length || 0;
      }

      self.bulkActionDetails.action = action;
      self.bulkActionDetails.userType = self.selectedUserType;
      self.showBulkConfirmModal = true;
    },

    cancelBulkAction() {
      var self = this;
      self.showBulkConfirmModal = false;
      self.bulkActionDetails = {
        action: '',
        userType: '',
        count: 0
      };
    },

    executeBulkAction() {
      var self = this;
      self.bulkActionInProgress = true;

      let isEnabled = true;
      let userIds = [];

      if (self.bulkActionDetails.action === 'enable_all') {
        isEnabled = true;
      } else if (self.bulkActionDetails.action === 'disable_all') {
        isEnabled = false;
      } else if (self.bulkActionDetails.action === 'enable_page') {
        isEnabled = true;
        // Get user IDs from current page
        userIds = self.allUsersModel.map(user => user.user_id);
      }

      const request = {
        user_type: self.selectedUserType,
        is_enabled: isEnabled
      };

      // Only add user_ids if we're doing a page-specific action
      if (self.bulkActionDetails.action === 'enable_page' && userIds.length > 0) {
        request.user_ids = userIds;
      }
      api
        .bulkUpdateUserPermissions(request)
        .then(function (response) {
          if (response.code == 200) {
            const actionText = isEnabled ? 'enabled' : 'disabled';
            const count = response.data.updated_count || 0;
            success(`Successfully ${actionText} Gift Card access for ${count} users`);

            // Reload the datatable to reflect changes
            setTimeout(function () {
              self.reloadDatatable();
            }, 500);
          } else {
            error(response.message || 'Failed to update user permissions');
          }
          self.bulkActionInProgress = false;
          self.showBulkConfirmModal = false;
        })
        .catch(function (err) {
          self.bulkActionInProgress = false;
          if (err.response && err.response.data && err.response.data.message) {
            error(err.response.data.message);
          } else {
            error("Failed to update user permissions");
          }
        });
    }
  }
};
</script>

<style scoped>
.modal-body-content {
  padding: 20px 0;
  text-align: center;
}

.confirmation-icon {
  font-size: 48px;
  color: #179346;
  margin-bottom: 16px;
}

.confirmation-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.confirmation-message {
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.6;
}

.confirmation-details {
  background-color: #f8f9fa;
  border-left: 4px solid #179346;
  padding: 16px;
  margin-bottom: 16px;
  text-align: left;
  border-radius: 4px;
}

.confirmation-details h5 {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.confirmation-details ul {
  margin: 0;
  padding-left: 20px;
}

.confirmation-details li {
  font-size: 13px;
  color: #555;
  margin-bottom: 6px;
}

.confirmation-note {
  font-size: 12px;
  color: #666;
  margin: 0 0 16px 0;
  font-style: italic;
  font-weight: 600;
}

.confirmation-terms {
  font-size: 11px;
  color: #666;
  margin: 0;
  line-height: 1.5;
  text-align: left;
}

.confirmation-terms a {
  color: #179346;
  text-decoration: none;
}

.confirmation-terms a:hover {
  text-decoration: underline;
}
</style>
